import type {
  Address,
  AddressResponse,
  CitiesResponse,
  LocationData,
  NeighborhoodsResponse,
} from "~/types/Data/user/address";
import type { BaseResponse } from "~/types/Data/Common/APIs";
import type { AddressFormData } from "~/types/Data/user/formData";

export default function useAddresses() {
  const addresses = useState<Address[] | null>("addresses", () => null);
  const addressToEdit = useState<Address | null>("address-to-edit", () => null);
  const loading = useState<boolean>("addresses-loading", () => false);
  const showEditForm = useState<boolean>(
    "addresses-show-edit-form",
    () => false,
  );
  const mapPickedLocation = useState<LocationData | null>(
    "map-picked-location",
    () => null,
  );

  const userToken = useCookie("hs-token");

  const isTheFirstAddress = computed(() => {
    return addresses.value?.length === 0 && !addressToEdit.value;
  });

  const getDefaultAddress = computed(() => {
    return addresses.value
      ? addresses.value.filter((add: Address) => add.default)
      : [];
  });

  const getOtherAddresses = computed(() => {
    return addresses.value
      ? addresses.value.filter((add: Address) => !add.default)
      : [];
  });

  const fetchAddresses = async () => {
    addresses.value = null;
    loading.value = true;
    try {
      const { res } = await useApi<AddressResponse>("/api/v1/addresses", {
        authToken: userToken.value,
      });

      addresses.value = res?.data?.sort((a) => (a.default ? -1 : 1)) ?? null;

      return res;
    } finally {
      loading.value = false;
    }
  };

  const updateAddress = async (
    data: Omit<AddressFormData, "map">,
    pinnedLocation?: { lat: number; lng: number },
  ) => {
    const body = {
      full_name: data.full_name,
      phone_number: data.mobile?.number,
      title: data.nickname ?? "",
      neighborhood_id: data.neighborhood,
      street: data.street,
      building: data.building,
      floor: data.floor || 0,
      apt: data.apartment,
      post_code: data.post_code,
      city_id: data.city,
      comment: data.comments,
      lat: pinnedLocation?.lat || 0,
      lng: pinnedLocation?.lng || 0,
      country_code: data.mobile?.country_code,
      is_someone_else: data.is_someone_else,
      recipient_name: data.recipient_name,
      recipient_company: data.recipient_company,
      recipient_mobile: data.recipient_mobile,
    };

    const { res } = await useApi<BaseResponse<Address>>(
      `/api/v1/addresses${
        addressToEdit.value ? `/${addressToEdit.value?.id}` : ""
      }`,
      {
        method: addressToEdit.value ? "PUT" : "POST",
        authToken: userToken.value,
        body,
      },
    );
    return res?.data;
  };

  const setAsDefault = async (addressId: string) => {
    loading.value = true;
    await useApi(`/api/v1/default-address/${addressId}`, {
      method: "PUT",
      authToken: userToken.value,
    });
    loading.value = false;
  };

  const deleteAddress = async (id: string) => {
    loading.value = true;
    try {
      await useApi(`/api/v1/addresses/${id}`, {
        method: "DELETE",
        authToken: userToken.value,
      });
      await fetchAddresses();
    } finally {
      loading.value = false;
    }
  };

  const fetchCities = async () => {
    const { res } = await useApi<CitiesResponse>(`/api/v1/city`);
    return res?.data ?? [];
  };

  const fetchAreas = async (city_id: string) => {
    const { res } = await useApi<NeighborhoodsResponse>(
      `/api/v1/neighborhoods?filter[city_id]=${city_id}`,
    );
    return res?.data ?? [];
  };

  return {
    addresses,
    addressToEdit,
    showEditForm,
    loading,
    mapPickedLocation,
    isTheFirstAddress,
    getDefaultAddress,
    getOtherAddresses,
    fetchAddresses,
    updateAddress,
    setAsDefault,
    deleteAddress,
    fetchCities,
    fetchAreas,
  };
}
